import { ref, nextTick } from 'vue';
import useNotificationApi from './useNotificationApi';
import { useMenuStore } from '~/stores/useMenuStore';
import { useNavigateToAsis } from '~/composables/useNavigateToAsis';
import { useCommon } from '~/composables/useCommon';
import { useAuthStore } from '~/stores/useAuthStore';
import { useUserStore } from '~/composables/user/useUserStore';
import { useUiToast } from '~/composables/useUiToast';
import { appT } from '~/composables/useAppI18n';

const toDateFromRealCreateDt = (value?: string | number | null): Date | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const raw = String(value).trim();
  if (!raw) return undefined;
  if (/^\d{8}$/.test(raw)) {
    const parsed = new Date(
      Number(raw.slice(0, 4)),
      Number(raw.slice(4, 6)) - 1,
      Number(raw.slice(6, 8)),
    );
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  if (/^\d{13}$/.test(raw)) {
    const parsed = new Date(Number(raw));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  if (/^\d{10}$/.test(raw)) {
    const parsed = new Date(Number(raw) * 1000);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const normalized = raw.includes('.') ? raw.replace(/\./g, '-') : raw;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

/** DB datetime(ms 타임스탬프 등) → YYYYMMDD 문자열 */
const parseRealCreateDt = (value?: string | number | null): string | undefined => {
  if (value == null || value === '') return undefined;

  const raw = typeof value === 'number' ? '' : String(value).trim();
  if (/^\d{8}$/.test(raw)) return raw;

  const date = toDateFromRealCreateDt(value);
  if (!date) return undefined;

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/** 알림 클릭 후 페이지 이동 시 목록·스크롤 유지용 */
const preserveNotificationPanel = ref(false);
const notificationScrollTop = ref(0);

const PLAN_NOTIFICATION_GBN_CDS = [
  'PLAN_ADD_COOP',
  'PLAN_TAKE_OVER',
  'PLAN_ADD_SHARE',
  'PLAN_TAKE_OVER2',
  'PLAN_DELIVERY_ALL',
  'PLAN_ADD_COOP_ALL',
  'PLAN_ADD_SHARE_ALL',
] as const;

const ACTIVITY_NOTIFICATION_GBN_CDS = ['ACTIVITY', 'ACTIVITY_REPLY'] as const;

const isPlanNotificationGbnCd = (gbnCd: string) =>
  (PLAN_NOTIFICATION_GBN_CDS as readonly string[]).includes(gbnCd);

const isActivityNotificationGbnCd = (gbnCd: string) =>
  (ACTIVITY_NOTIFICATION_GBN_CDS as readonly string[]).includes(gbnCd);

/** PLAN gbnCd → 협업 모달 탭 (C:협업 / S:공유 / M:전달) */
const resolvePlanNotifyTabId = (gbnCd: string): string => {
  if (['PLAN_ADD_SHARE', 'PLAN_ADD_SHARE_ALL'].includes(gbnCd)) return 'S';
  if (['PLAN_TAKE_OVER', 'PLAN_TAKE_OVER2', 'PLAN_DELIVERY_ALL'].includes(gbnCd)) return 'M';
  return 'C';
};

const openPlanNotification = async (gbnCd: string): Promise<boolean> => {
  const { useCollaborationStore } = await import('~/composables/useCollaborationStore');
  const store = useCollaborationStore();
  store.planNotifyRequest.value = { tabId: resolvePlanNotifyTabId(gbnCd) };

  if (useRoute().path !== '/task/collaboration') {
    await navigateTo('/task/collaboration');
  }
  return true;
};

const openActivityNotification = async (linkId?: string, path?: string): Promise<boolean> => {
  const planId = linkId?.trim();
  if (!planId) {
    useUiToast().showToast('error', appT('common.notification.taskCardNotFound'));
    return false;
  }

  const { useCollaborationStore } = await import('~/composables/useCollaborationStore');
  const store = useCollaborationStore();
  store.activityNotifyRequest.value = { planId, activityPath: path };

  if (useRoute().path !== '/task/collaboration') {
    await navigateTo('/task/collaboration');
  }
  return true;
};

/**
 * AS-IS → TO-BE 딥링크 (collaboration)
 * 쿼리를 기존 planNotifyRequest / activityNotifyRequest 로 변환 (페이지 이동 없음)
 * @returns 'plan' | 'activity' | null
 */
const applyCollaborationNotifyFromQuery = async (params: {
  notifyGbnCd?: string;
  gbnCd?: string;
  planId?: string;
  activityPath?: string;
}): Promise<'plan' | 'activity' | null> => {
  const notifyGbnCd = (params.notifyGbnCd || '').trim();
  const gbnCd = (params.gbnCd || '').trim();
  if (notifyGbnCd !== 'AVT' || !gbnCd) return null;

  if (isPlanNotificationGbnCd(gbnCd)) {
    const { useCollaborationStore } = await import('~/composables/useCollaborationStore');
    useCollaborationStore().planNotifyRequest.value = { tabId: resolvePlanNotifyTabId(gbnCd) };
    return 'plan';
  }

  if (isActivityNotificationGbnCd(gbnCd)) {
    const planId = (params.planId || '').trim();
    if (!planId) {
      useUiToast().showToast('error', appT('common.notification.taskCardNotFound'));
      return null;
    }
    const { useCollaborationStore } = await import('~/composables/useCollaborationStore');
    useCollaborationStore().activityNotifyRequest.value = {
      planId,
      activityPath: (params.activityPath || '').trim() || undefined,
    };
    return 'activity';
  }

  return null;
};

/**
 * AS-IS → TO-BE 딥링크 (reports)
 * 쿼리를 기존 reportNotifyRequest 로 변환 (페이지 이동 없음)
 */
const applyReportNotifyFromQuery = async (params: {
  notifyGbnCd?: string;
  gbnCd?: string;
  searchDate?: string;
}): Promise<boolean> => {
  const notifyGbnCd = (params.notifyGbnCd || '').trim();
  const gbnCd = (params.gbnCd || '').trim();
  const searchDate = (params.searchDate || '').trim();
  if (notifyGbnCd !== 'RPT' || !gbnCd || !/^\d{8}$/.test(searchDate)) return false;

  const { useReportsStore } = await import('~/composables/useReportsStore');
  useReportsStore().reportNotifyRequest.value = { searchDate };
  return true;
};


/**
 * 알림 Store
 * - 알림 목록 조회, 읽음 처리, 삭제 기능
 * - 무한 스크롤 지원
 */
export const useNotificationStore = () => {
  // 상태
  const notifications = ref<any[]>([]);

  // API
  const $api = useNotificationApi();
  const menuStore = useMenuStore();
  const route = useRoute();
  const { navigateToAsis } = useNavigateToAsis();
  const { isNotEmpty } = useCommon();

  const getAllMenuItems = (): any[] => {
    const allMenuItems: any[] = [];
    for (const section of menuStore.sections) {
      if (section.items) {
        allMenuItems.push(...section.items);
      }
      if (section.submenus) {
        for (const submenu of section.submenus) {
          if (submenu.items) {
            allMenuItems.push(...submenu.items);
          }
        }
      }
    }
    return allMenuItems;
  };

  const normalizeTobePath = (rawPath?: string, fallback = '') => {
    let targetPath = (rawPath || '').trim();
    if (!isNotEmpty(targetPath) || targetPath === '#') {
      return fallback;
    }
    if (targetPath.indexOf('/') !== 0) {
      targetPath = `/${targetPath}`;
    }
    // AS-IS·알림 API: /app/performance/kpi → Nuxt 라우트 /performance/kpi
    targetPath = targetPath.replace(/^\/app(?=\/|$)/, '') || fallback;
    targetPath = targetPath.replace(/^\/screens/, '');
    return targetPath.split('?')[0] || fallback;
  };

  const findMenuItemByPath = (targetPath?: string): any | null => {
    const normalizedPath = normalizeTobePath(targetPath);
    if (!normalizedPath || !menuStore.sections?.length) {
      return null;
    }

    const allMenuItems = getAllMenuItems();

    let bestMatch: any | null = null;
    let maxMatchLength = 0;

    for (const item of allMenuItems) {
      const itemPath = normalizeTobePath(item.path);
      if (itemPath && normalizedPath.startsWith(itemPath) && itemPath.length > maxMatchLength) {
        bestMatch = item;
        maxMatchLength = itemPath.length;
      }

      if (item.tabs && Array.isArray(item.tabs)) {
        for (const tab of item.tabs) {
          const tabPath = normalizeTobePath(tab.path);
          if (tabPath && tabPath === normalizedPath) {
            return item;
          }
        }
      }
    }

    return bestMatch;
  };

  const findMenuItemByPgmId = (targetPgmId?: string): any | null => {
    if (!targetPgmId || !menuStore.sections?.length) {
      return null;
    }

    return getAllMenuItems().find(item => item.pgmId === targetPgmId) || null;
  };

  const resolveMenuItem = (path?: string, pgmId?: string) => {
    return findMenuItemByPath(path) || findMenuItemByPgmId(pgmId);
  };

  const handleAvtWithLinkId = async (linkId: string, path?: string, pgmId?: string) => {
    const menuItem = resolveMenuItem(path, pgmId);

    if (menuItem?.viewType === 'T') {
      const menuPath = menuItem.path && menuItem.path !== '#' ? menuItem.path : '';
      const targetPath = normalizeTobePath(menuPath || path, '/task/home');

      if (route.path === targetPath) {
        const { useCollaborationStore } = await import('~/composables/useCollaborationStore');
        await useCollaborationStore().openCollaborationTaskModal(linkId);
        return;
      }

      await navigateTo({ path: targetPath, query: { planId: linkId } });
      return;
    }

    const { useCollaborationStore } = await import('~/composables/useCollaborationStore');
    await useCollaborationStore().openCollaborationTaskModal(linkId);
  };

  const navigateByPathOrViewType = async (path?: string, pgmId?: string) => {
    const normalizedPath = normalizeTobePath(path);
    const menuItem = resolveMenuItem(normalizedPath || path, pgmId);

    if (menuItem?.viewType === 'A') {
      const hash = pgmId || menuItem?.pgmId || '';
      if (hash) {
        navigateToAsis({
          entryPath: '/main.do',
          hash,
        });
        return;
      }
    }

    if (normalizedPath) {
      await navigateTo(normalizedPath);
    }
  };

  const syncUnreadCntFromServer = async () => {
    await useAuthStore().refreshUnreadNotificationCnt();
  };

  // 데이터 변환 함수
  const transformNotification = (item: any) => ({
    id: item.notifyId,
    sender: item.senderNm,
    title: item.title,
    notifyGbnCd: item.notifyGbnCd,
    gbnCd: item.gbnCd,
    linkId: item.linkId,
    path: item.path,
    pgmId: item.pgmId || item.pgmID || item.menuPgmId || '',
    content: item.body,
    time: item.createDt,
    read: item.readYn === 'Y',
    status: 'info',
    realCreateDt: parseRealCreateDt(item.realCreateDt ?? item.REAL_CREATE_DT),
  });

  // 알림 목록 조회 (초기 로드)
  const loadNotifications = async () => {
    try {
      const response = await $api.fetchNotifications(1);
      notifications.value = response.list.map(transformNotification);
    } catch (error) {
      console.error('알림 목록 조회 실패:', error);
    }
  };

  // 알림 목록 추가 로드 (무한 스크롤용)
  const loadMoreNotifications = async (page: number) => {
    try {
      const response = await $api.fetchNotifications(page);
      const newNotifications = response.list.map(transformNotification);

      // 기존 목록에 새 알림 추가
      if (newNotifications.length > 0) {
        notifications.value = [...notifications.value, ...newNotifications];
      }

      return {
        hasMore: response.list.length >= 10, // 10개씩 로드한다고 가정
        data: newNotifications,
      };
    } catch (error) {
      console.error('알림 추가 로드 실패:', error);
      return {
        hasMore: false,
        data: [],
      };
    }
  };

  // 알림 목록 초기화
  const resetNotifications = () => {
    notifications.value = [];
  };

  // 알림 삭제
  const deleteNotification = async (notifyGbnCd: string, notifyId: string) => {
    try {
      const res = await $api.deleteNotification(notifyGbnCd, notifyId);
      if (res?.result !== 'SUCCESS') return;

      if (notifyGbnCd === 'ALL') {
        notifications.value = [];
      } else {
        notifications.value = notifications.value.filter(item => item.id != notifyId);
      }
      await syncUnreadCntFromServer();
    } catch (error) {
      console.error('알림 삭제 실패:', error);
    }
  };

  // 알림 읽음 처리
  const updateNotification = async (notifyGbnCd: string, notifyId: string) => {
    try {
      if (notifyGbnCd === 'SYS') return;

      const res = await $api.updateNotification(notifyGbnCd, notifyId);
      if (res?.result !== 'SUCCESS') return;

      if (notifyGbnCd === 'ALL') {
        notifications.value = notifications.value.map(item => {
          if (item.notifyGbnCd != 'SYS') {
            item.read = true;
          }
          return item;
        });
      } else {
        notifications.value = notifications.value.map(item => {
          if (item.notifyGbnCd != 'SYS') {
            item.read = item.id == notifyId ? true : item.read;
          }
          return item;
        });
      }
      await syncUnreadCntFromServer();
    } catch (error) {
      console.error('알림 읽음 처리 실패:', error);
    }
  };

  // 알림 읽기 및 카드 상세 열기 통합 핸들러
  const handleReadNotification = async (
    notifyGbnCd: string,
    gbnCd: string,
    notifyId: string,
    linkId?: string,
    path?: string,
    realCreateDt?: string,
    pgmId?: string,
  ) => {
    const { modals, openModal } = useUserStore();
    const keepNotificationOpen = modals.notification;
    preserveNotificationPanel.value = keepNotificationOpen;

    try {
      // 1. 알림 읽음 처리
      await updateNotification(notifyGbnCd, notifyId);

      let navigated = false;
      if (notifyGbnCd === 'SYS') {
        if (linkId) {
          window.open(linkId);
        } else if (path) {
          await navigateByPathOrViewType(path, pgmId);
          navigated = true;
        }
      } else if (notifyGbnCd === 'AVT') {
        if (isPlanNotificationGbnCd(gbnCd)) {
          navigated = await openPlanNotification(gbnCd);
        } else if (isActivityNotificationGbnCd(gbnCd)) {
          navigated = await openActivityNotification(linkId, path);
        }
      } else if (notifyGbnCd === 'RPT') {
        if (realCreateDt && /^\d{8}$/.test(realCreateDt)) {
          const { useReportsStore } = await import('~/composables/useReportsStore');
          useReportsStore().reportNotifyRequest.value = { searchDate: realCreateDt };
        }
        if (useRoute().path !== '/task/reports/view') {
          await navigateTo('/task/reports/view');
        }
        navigated = true;
      } else if (notifyGbnCd === 'INQ') {
        //문의하기는 SR연동으로 변경되면서 클릭이벤트X
      } else if (notifyGbnCd === 'OBJ') {
        // OBJ: viewType A(ASIS)로 내려와도 T(TOBE) 경로로 이동
        if (useRoute().path !== '/performance/kpi') {
          await navigateTo('/performance/kpi');
        }
        navigated = true;
      } else if (path) {
        await navigateByPathOrViewType(path, pgmId);
        navigated = true;
      }

      // navigateTo 시 checkAuth가 auth.user를 덮어쓸 수 있어 서버 기준으로 재동기화
      if (navigated) {
        await syncUnreadCntFromServer();
      }

      // 페이지 이동 시 UiModal이 라우트 변경으로 닫히므로 알림창만 다시 연다
      if (keepNotificationOpen) {
        await nextTick();
        openModal('notification');
        if (!navigated) {
          preserveNotificationPanel.value = false;
        }
      } else {
        preserveNotificationPanel.value = false;
      }
    } catch (error) {
      preserveNotificationPanel.value = false;
      console.error('알림 처리 실패:', error);
    }
  };

  return {
    notifications,
    loadNotifications,
    loadMoreNotifications,
    resetNotifications,
    updateNotification,
    deleteNotification,
    handleReadNotification,
    applyCollaborationNotifyFromQuery,
    applyReportNotifyFromQuery,
    isPlanNotificationGbnCd,
    isActivityNotificationGbnCd,
    resolvePlanNotifyTabId,
    preserveNotificationPanel,
    notificationScrollTop,
  };
};
